class MTMCalculator {
  static loggedScenarios = new Set()

  static logOnce(key, message) {
    if (!this.loggedScenarios.has(key)) {
      this.loggedScenarios.add(key)
      console.log(message)
    }
  }

  static calculateVanillaExoticTradePnL(trade, priceRates) {
    let calculationCount = 0

    // Calculate client side PnL
    let premUSD = this.calculateVanillaExoticSidePnL(trade, priceRates)
    if (premUSD !== null) {
      // If we BUY, we are paying the premium, so invert it
      if (trade.side === 'BUY') {
        premUSD = premUSD * -1 // Invert for BUY side
        // If we SELL, we are receiving the premium, so keep it as is
      } else {
        premUSD = premUSD * 1 // Keep SELL side as is
      }
      calculationCount++
    }

    if (calculationCount > 0) {
      return {
        premium: premUSD,
        calculation_count: calculationCount
      }
    }
  }

  static calculateVanillaExoticSidePnL(trade, priceRates) {
    const logKey = `${trade.id}`
    let premium_amount_usd = 0
    const premium_currency = trade.premium_currency
    const premium_amount = trade.premium_amount
    this.logOnce(
      logKey + '_0',
      `Calculating PnL for trade ${trade.id} with premium ${premium_amount} ${premium_currency}`
    )
    // Case 1: Premium already in USD, no conversion needed
    if (premium_currency === 'USD') {
      this.logOnce(
        logKey + '_1',
        `Premium already in USD — Side: ${trade.side}, Amount: ${premium_amount}`
      )
      premium_amount_usd = trade.premium_amount
    } else {
      // Case 2: Attempt to convert to USD using current snapshot
      const usdToPremium = `USD${premium_currency}`.toUpperCase()
      const premiumToUSD = `${premium_currency}USD`.toUpperCase()

      if (!priceRates[premiumToUSD] && !priceRates[usdToPremium]) {
        this.logOnce(
          logKey + '_1',
          `FX rate for ${premium_currency}/USD not found; skipping trade ${trade.id}.`
        )
        return null
      }

      // Case 2a: Direct premium/USD rate available (e.g., EURUSD = 1.10)
      if (priceRates[premiumToUSD]) {
        const converted = priceRates[premiumToUSD].LAST_PRICE * premium_amount
        premium_amount_usd = converted
        this.logOnce(
          logKey + '_1',
          `Converted premium via ${premium_currency}USD: ${premium_amount} * ${priceRates[premiumToUSD].LAST_PRICE} = ${converted}`
        )
        // Case 2b: Inverted USD/premium rate available (e.g., USDEUR = 0.91 → invert)
      } else if (priceRates[usdToPremium]) {
        const converted = (1 / priceRates[usdToPremium].LAST_PRICE) * premium_amount
        premium_amount_usd = converted
        this.logOnce(
          logKey + '_1',
          `Converted premium via inverted USD${premium_currency}: ${premium_amount} / ${priceRates[usdToPremium].LAST_PRICE} = ${converted}`
        )
      }
    }
    return premium_amount_usd
  }

  // New method to calculate MTM for a complete trade (client + market maker)
  static calculateSpotNdfForwardTradeMTM(trade, priceRates) {
    let totalMTM = 0
    let calculationCount = 0

    // Calculate client side MTM
    const clientMTM = this.calculateSpotNdfForwardSideMTM(trade, priceRates, 'client')
    if (clientMTM !== null) {
      totalMTM += clientMTM
      calculationCount++
    }

    // Calculate market maker side MTM
    const marketMakerMTM = this.calculateSpotNdfForwardSideMTM(trade, priceRates, 'market_maker')
    if (marketMakerMTM !== null) {
      totalMTM += marketMakerMTM
      calculationCount++
    }

    if (calculationCount > 0) {
      return {
        total_mtm: totalMTM,
        client_mtm: clientMTM,
        market_maker_mtm: marketMakerMTM,
        calculation_count: calculationCount
      }
    }

    return null
  }

  // Modified method to calculate MTM for one side (client OR market_maker)
  // This calculates the USD MTM for Spot, NDF and Forward trades
  static calculateSpotNdfForwardSideMTM(trade, priceRates, side) {
    const logKey = `${trade.id}`

    // 1. Determine which side of the trade we're calculating (client or market_maker)
    // and extract the relevant quantity and trade price fields
    const quantityField = side === 'client' ? 'client_quantity' : 'market_maker_quantity'
    const priceField = side === 'client' ? 'client_price' : 'market_maker_price'

    const quantity = parseFloat(trade[quantityField])
    let tradePrice = parseFloat(trade[priceField])
    const baseCurrency = trade.base_currency
    const counterCurrency = trade.counter_currency

    // Skip if this side doesn't have data - all will have an empty side
    if (!quantity || !tradePrice || !baseCurrency || !counterCurrency) {
      return null
    }

    // Construct currency pair strings
    const currencyPair = `${baseCurrency}${counterCurrency}`.toUpperCase()
    const invertedCurrencyPair = `${counterCurrency}${baseCurrency}`.toUpperCase()

    // Spot rate is the current market rate from our live broadcasting
    let spotRate = null
    let tradePriceRaw = null // In case we need to log the original value before inversion

    // Case 1: Our traded pair matches the market quoting convention directly
    if (priceRates[currencyPair]) {
      spotRate = priceRates[currencyPair].LAST_PRICE
      this.logOnce(
        logKey + '_1',
        `######################\n${trade.id}: [${side}]\nQuantity: ${quantity}\nTrade Price: ${tradePrice}\nCcy Pair: ${currencyPair}\n✅ Matches convention. Spot: ${spotRate}\n`
      )
      // Case 2: Our traded pair is the inverse of the quoting convention, so we flip the prices
      // Invert both spot and trade price because the market quotes the inverse pair.
      // This ensures both rates are expressed as 'counter per base' to match trade quantity units.
    } else if (priceRates[invertedCurrencyPair]) {
      spotRate = 1 / priceRates[invertedCurrencyPair].LAST_PRICE
      tradePriceRaw = tradePrice
      tradePrice = 1 / tradePriceRaw

      this.logOnce(
        logKey + '_1',
        `${trade.id}: [${side}]\nQuantity: ${quantity}\nTrade Price: ${tradePriceRaw}\nCcy Pair: ${currencyPair}\n❌ Does not match convention (${invertedCurrencyPair}). Spot: ${spotRate}\nTrade Price inverted to match convention: ${tradePrice}\n`
      )
    }

    // If no spot rate found for either pair, log and exit
    if (!spotRate) {
      console.error(`Could not find ${currencyPair} in rates list.`)
      return null
    }

    let usdConversionRate = null
    let mtm_usd = null

    // MTM is calculated in counter currency
    const rateDifference = spotRate - tradePrice
    const mtm_counter = quantity * rateDifference

    this.logOnce(
      logKey + '_2',
      `Rate Difference: ${rateDifference}\nMTM in Counter Currency: ${mtm_counter} - ${counterCurrency}\n`
    )

    // Case 1: If counter currency is USD, MTM is already in USD
    if (counterCurrency === 'USD') {
      usdConversionRate = 1
      this.logOnce(logKey + '_3', `Counter Currency is USD. No conversion needed.\n`)

      // Case 2: If base is USD, spot rate gives us counter per USD, so divide to get MTM in USD
    } else if (baseCurrency === 'USD') {
      usdConversionRate = spotRate
      this.logOnce(
        logKey + '_3',
        `Base Currency is USD. Conversion is spotRate because MTM is in counter: ${usdConversionRate}.\n`
      )

      // Case 3: Neither side is USD — use cross rate to convert counter to USD
    } else {
      const usdCrossSymbol = `USD${counterCurrency}`.toUpperCase()
      const crossUsdSymbol = `${counterCurrency}USD`.toUpperCase()

      // If we have USD as base (e.g. USDJPY), divide to convert counter → USD
      if (priceRates[usdCrossSymbol]) {
        usdConversionRate = priceRates[usdCrossSymbol].LAST_PRICE
        this.logOnce(
          logKey + '_3',
          `Found USD cross rate: ${usdCrossSymbol} - ${usdConversionRate}`
        )

        // If we have USD as counter (e.g. JPYUSD), multiply directly
      } else if (priceRates[crossUsdSymbol]) {
        usdConversionRate = 1 / priceRates[crossUsdSymbol].LAST_PRICE
        this.logOnce(
          logKey + '_3',
          `Found USD cross rate: ${crossUsdSymbol} - ${usdConversionRate}`
        )
      } else {
        console.error(`Could not find USD cross rate for ${counterCurrency}`)
        return null
      }
    }

    // Convert MTM from counter currency to USD
    mtm_usd = mtm_counter / usdConversionRate

    this.logOnce(
      logKey + '_4',
      `MTM Counter: ${mtm_counter}\nMTM USD: ${mtm_usd} (mtm_counter / usdconversion)`
    )
    return mtm_usd
  }

  static calculateSwapTradeMTM(trade, priceRates) {
    let totalMTM = 0
    let calculationCount = 0

    // Calculate client side MTM
    const clientMTM = this.calculateSwapSideMTM(trade, priceRates, 'client')
    if (clientMTM !== null) {
      totalMTM += clientMTM
      calculationCount++
    }

    // Calculate market maker side MTM
    const marketMakerMTM = this.calculateSwapSideMTM(trade, priceRates, 'market_maker')
    if (marketMakerMTM !== null) {
      totalMTM += marketMakerMTM
      calculationCount++
    }

    if (calculationCount > 0) {
      return {
        total_mtm: totalMTM,
        client_mtm: clientMTM,
        market_maker_mtm: marketMakerMTM,
        calculation_count: calculationCount
      }
    }

    return null
  }

  // Method to calculate MTM for a Swap trade by summing near and far legs
  // This calculates the USD MTM for Swap trades
  static calculateSwapSideMTM(trade, priceRates, side) {
    const logKey = `${trade.id}`

    // Extract leg-specific fields for the chosen side (client or market_maker)
    const quantityField = side === 'client' ? 'client_quantity' : 'market_maker_quantity'
    const priceField = side === 'client' ? 'client_price' : 'market_maker_price'
    const farQuantityField = 'far_leg_quantity'
    const farPriceField = 'far_price'

    const quantity = parseFloat(trade[quantityField])
    let tradePrice = parseFloat(trade[priceField])
    const farQuantity = parseFloat(trade[farQuantityField])
    let farPrice = parseFloat(trade[farPriceField])
    const baseCurrency = trade.base_currency
    const counterCurrency = trade.counter_currency

    // Skip if this side doesn't have data - all will have an empty side
    if (
      !quantity ||
      !tradePrice ||
      !baseCurrency ||
      !counterCurrency ||
      !farPrice ||
      !farQuantity
    ) {
      return null
    }

    // Create currency pairs
    const currencyPair = `${baseCurrency}${counterCurrency}`.toUpperCase()
    const invertedCurrencyPair = `${counterCurrency}${baseCurrency}`.toUpperCase()

    // Find market rate
    let spotRate = null
    let tradePriceRaw = null
    let farTradePriceRaw = null

    // --- Case 1: Traded pair matches quoting convention ---
    // Use the spot rate directly as it's quoted in the same direction as the trade
    if (priceRates[currencyPair]) {
      spotRate = priceRates[currencyPair].LAST_PRICE

      this.logOnce(
        logKey + '_1',
        `######################\n${trade.id}: [${side}]\nQuantity: ${quantity}\nTrade Price: ${tradePrice}\nFar Quantity: ${farQuantity}\nFar Price: ${farPrice}\nCcy Pair: ${currencyPair}\n✅ Matches convention. Spot: ${spotRate}`
      )

      // --- Case 2: Traded pair does not match quoting convention ---
      // Invert the market spot rate and the trade prices to ensure all rates are expressed as 'counter per base'
    } else if (priceRates[invertedCurrencyPair]) {
      spotRate = 1 / priceRates[invertedCurrencyPair].LAST_PRICE
      // These are for logging purposes
      tradePriceRaw = tradePrice
      farTradePriceRaw = farPrice

      tradePrice = 1 / tradePriceRaw
      farPrice = 1 / farTradePriceRaw

      this.logOnce(
        logKey + '_1',
        `${trade.id}: [${side}]\nQuantity: ${quantity}\nTrade Price: ${tradePriceRaw}\nFar Quantity: ${farQuantity}\nFar Price: ${farTradePriceRaw}\nCcy Pair: ${currencyPair}\n❌ Does not match convention (${invertedCurrencyPair}). Spot: ${spotRate}\nTrade Price inverted to match convention: ${tradePrice}\nFar Price inverted to match convention: ${farPrice}`
      )
    }

    if (!spotRate) {
      console.error(`Could not find ${currencyPair} in rates list.`)
      return null
    }

    let mtm_usd = null
    let mtm_near_usd = null
    let mtm_far_usd = null

    // Calculate the difference between market rate and traded rate for each leg
    const rateDifferenceNear = spotRate - tradePrice
    const rateDifferenceFar = spotRate - farPrice
    // Multiply rate difference by quantity to get MTM in counter currency
    const mtm_near_counter = quantity * rateDifferenceNear
    const mtm_far_counter = farQuantity * rateDifferenceFar

    this.logOnce(
      logKey + '_2',
      `Rate Near Difference: ${rateDifferenceNear}\nRate Far Difference: ${rateDifferenceFar}\nMTM Near in Counter Currency: ${mtm_near_counter} - ${counterCurrency}\nMTM Far in Counter Currency: ${mtm_far_counter} - ${counterCurrency}`
    )

    // --- USD Conversion Logic ---
    // Convert MTM from counter currency to USD based on currency position
    let usdConversionRate = null

    // --- Case 1: Counter currency is USD ---
    if (counterCurrency === 'USD') {
      usdConversionRate = 1 // No conversion needed if counter currency is USD
      this.logOnce(logKey + '_3', `Counter Currency is USD. No conversion needed.`)

      // --- Case 2: Base currency is USD ---
      // Spot rate gives us counter per 1 USD, so use it directly
    } else if (baseCurrency === 'USD') {
      usdConversionRate = spotRate
      this.logOnce(
        logKey + '_3',
        `Base Currency is USD. Conversion is spotRate because MTM is in counter: ${usdConversionRate}.`
      )

      // --- Case 3: Neither base nor counter is USD ---
      // Look for a USD cross rate to convert counter to USD
    } else {
      const usdCrossSymbol = `USD${counterCurrency}`.toUpperCase()
      const crossUsdSymbol = `${counterCurrency}USD`.toUpperCase()

      // If USD is base in quote, divide to convert counter to USD (the division occurs below)
      if (priceRates[usdCrossSymbol]) {
        usdConversionRate = priceRates[usdCrossSymbol].LAST_PRICE
        this.logOnce(
          logKey + '_3',
          `Found USD cross rate: ${usdCrossSymbol} - ${usdConversionRate}`
        )

        // If USD is counter in quote, invert rate to convert counter to USD
      } else if (priceRates[crossUsdSymbol]) {
        usdConversionRate = 1 / priceRates[crossUsdSymbol].LAST_PRICE
        this.logOnce(
          logKey + '_3',
          `Found USD cross rate: ${crossUsdSymbol} - ${usdConversionRate}`
        )
      } else {
        console.error(`Could not find USD cross rate for ${counterCurrency}`)
        return null
      }
    }

    // Convert MTM in counter currency to USD by dividing by the USD conversion rate
    mtm_near_usd = mtm_near_counter / usdConversionRate
    mtm_far_usd = mtm_far_counter / usdConversionRate

    mtm_usd = mtm_near_usd + mtm_far_usd

    this.logOnce(
      logKey + '_4',
      `MTM Near Counter: ${mtm_near_counter}\nMTM Far Counter: ${mtm_far_counter}\nMTM Near USD: ${mtm_near_usd} (mtm_near_counter / usdconversion)\nMTM Far USD: ${mtm_far_usd} (mtm_far_counter / usdconversion)\nTOTAL MTM USD: ${mtm_usd} (mtm_near_usd + mtm_far_usd)`
    )
    return mtm_usd
  }
}

export default MTMCalculator
